import { useLocation } from 'react-router-dom'
import { useConfig } from '@/lib/siteData'
import { routeSeo as defaultRouteSeo } from '@/config-defaults'
import { useDocumentMeta } from './useDocumentMeta'

type DocumentMetaOptions = Parameters<typeof useDocumentMeta>[0]

/**
 * Per-route <title> + description, resolved from the admin-editable
 * `route_seo` row — the same row the server SEO template reads.
 *
 * Why this exists: the server writes a per-route <head> for crawlers, and then
 * `useDocumentMeta` overwrites it after hydration with a string hardcoded in
 * the page component. Two sources of truth for the same two strings means the
 * browser tab and the crawler can disagree, and an admin editing page titles
 * would only move one of them. Pages call this instead so both layers resolve
 * from one place.
 *
 * The argument is the page's own fallback, used when `route_seo` has no entry
 * for this path (or the entry omits a field). Anything else — ogImage, jsonLd,
 * robots, ogType — passes straight through to `useDocumentMeta`.
 *
 * `route_seo` titles are absolute: they already carry the brand where it
 * belongs, so they must not get the " — Chinedu Koggu" suffix appended a
 * second time.
 */
export function useRouteMeta(fallback: DocumentMetaOptions = {}): void {
  const { pathname } = useLocation()
  const cfg = useConfig('route_seo', defaultRouteSeo)

  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname
  const entry = (cfg.routes ?? []).find((r) => r.path === normalized)

  useDocumentMeta({
    ...fallback,
    ...(entry?.title ? { titleAbsolute: entry.title, title: undefined } : {}),
    ...(entry?.description ? { description: entry.description } : {}),
  })
}
